import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { hideBackButton, setBackButton } from '../../utils/telegram';
import KnowledgeContent, { extractKnowledgeAttachmentIds } from '../../components/knowledge/KnowledgeContent';
import KnowledgeAttachments from '../../components/knowledge/KnowledgeAttachments';
import KnowledgeMedia from '../../components/knowledge/KnowledgeMedia';

export default function KnowledgeArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/app/knowledge/articles/${id}`);
      setArticle(response.data);
    } catch (err) {
      if ([403, 404].includes(err.response?.status)) {
        setError('Статья не найдена или у вас нет к ней доступа.');
      } else {
        setError(err.response?.data?.detail || 'Не удалось загрузить статью');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const goBack = () => navigate('/app/knowledge');
    setBackButton(goBack);
    return () => hideBackButton(goBack);
  }, [navigate]);

  const embeddedIds = new Set(extractKnowledgeAttachmentIds(article?.content));
  const remainingAttachments = (article?.attachments || []).filter(attachment => !embeddedIds.has(String(attachment.id)));

  return (
    <div className="app-page knowledge-app-page knowledge-article-page">
      <header className="app-header knowledge-app-header">
        <button type="button" className="btn-back" onClick={() => navigate('/app/knowledge')} aria-label="Вернуться к списку статей">←</button>
        <h1>База знаний</h1>
      </header>

      {loading ? (
        <div className="knowledge-app-state"><span className="spinner" /><p>Загружаем статью...</p></div>
      ) : error ? (
        <main className="knowledge-app-state error">
          <div aria-hidden="true">⚠️</div>
          <h2>Статья недоступна</h2>
          <p>{error}</p>
          <div className="knowledge-state-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/app/knowledge')}>К списку</button>
            <button type="button" className="btn-primary" onClick={load}>Повторить</button>
          </div>
        </main>
      ) : (
        <article className="knowledge-article-view">
          {article.cover && <KnowledgeMedia attachment={article.cover} variant="cover" className="knowledge-article-cover" />}
          <header className="knowledge-article-view-header">
            {article.category_name && <div className="knowledge-article-category">{article.category_path_label || article.category_name}</div>}
            <h1>{article.title}</h1>
            {article.summary && <p>{article.summary}</p>}
            {article.updated_at && (
              <time dateTime={article.updated_at}>Обновлено {formatDate(article.updated_at)}</time>
            )}
          </header>
          <KnowledgeContent content={article.content} attachments={article.attachments || []} />
          <KnowledgeAttachments attachments={remainingAttachments} title="Дополнительные файлы" />
        </article>
      )}
    </div>
  );
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}
